/**
 * Calendar Component
 * Main calendar with view switching (day/week/month)
 */

import { useState } from 'react';
import type { AppointmentWithDetails } from '../../hooks/useAppointments';
import { CalendarMonthView } from './CalendarMonthView';
import { CalendarWeekView } from './CalendarWeekView';
import { CalendarDayView } from './CalendarDayView';
import {
  getWeekStart,
  getIcelandicMonthName,
  getDateFromISO,
  formatDateShort,
} from '../../utils/calendar';

export type CalendarView = 'day' | 'week' | 'month';

interface CalendarProps {
  appointments: AppointmentWithDetails[];
  onAppointmentClick: (appointment: AppointmentWithDetails) => void;
  onTimeSlotClick: (date: string, time: string) => void;
  onDateClick: (date: Date) => void;
}

export function Calendar({
  appointments,
  onAppointmentClick,
  onTimeSlotClick,
  onDateClick,
}: CalendarProps) {
  const [view, setView] = useState<CalendarView>('week');
  const [currentDate, setCurrentDate] = useState(new Date());

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const weekStart = getWeekStart(currentDate);

  // Navigation handlers
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Get title based on view
  const getTitle = () => {
    if (view === 'day') {
      return formatDateShort(currentDate);
    } else if (view === 'week') {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 4);
      return `${formatDateShort(weekStart)} - ${formatDateShort(weekEnd)}`;
    } else {
      return `${getIcelandicMonthName(currentMonth)} ${currentYear}`;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Calendar header */}
      <div className="flex items-center justify-between mb-4">
        {/* Navigation */}
        <div className="flex items-center gap-4">
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Í dag
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevious}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
              {getTitle()}
            </h2>
            <button
              onClick={goToNext}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* View switcher */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setView('day')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === 'day'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Dagur
          </button>
          <button
            onClick={() => setView('week')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === 'week'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Vika
          </button>
          <button
            onClick={() => setView('month')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mánuður
          </button>
        </div>
      </div>

      {/* Calendar view */}
      <div className="flex-1 overflow-hidden">
        {view === 'month' && (
          <CalendarMonthView
            year={currentYear}
            month={currentMonth}
            appointments={appointments}
            onAppointmentClick={onAppointmentClick}
            onDateClick={(date) => {
              setCurrentDate(date);
              setView('day');
              onDateClick(date);
            }}
          />
        )}
        {view === 'week' && (
          <CalendarWeekView
            weekStart={weekStart}
            appointments={appointments}
            onAppointmentClick={onAppointmentClick}
            onTimeSlotClick={onTimeSlotClick}
          />
        )}
        {view === 'day' && (
          <CalendarDayView
            date={currentDate}
            appointments={appointments}
            onAppointmentClick={onAppointmentClick}
            onTimeSlotClick={(time) => {
              const dateStr = getDateFromISO(currentDate.toISOString());
              onTimeSlotClick(dateStr, time);
            }}
          />
        )}
      </div>
    </div>
  );
}
